import { XSD_APPLICANT_FIELDS, XSD_SIMPLE_TYPES, XsdFacets, XsdField } from './fieldSpec'
import { message } from './messages'
import { IssuePath, Page, RawIssue, present } from './types'

/**
 * The XSD layer of the rule set: lengths, patterns, permitted values and required elements, straight from the
 * generated field spec, applied to the stored application data.
 */

/** The XSD element names that differ from the stored data's keys (DBS-307: the portal stores NiNumber) */
const DATA_KEYS: Record<string, string> = { NINumber: 'NiNumber' }

/** Which applicant page each top-level field (or AdditionalApplicantDetails sub-field) belongs to */
const FIELD_PAGES: Record<string, Page> = {
  'Title': 'names', 'Forename': 'names', 'Middlenames': 'names', 'PresentSurname': 'names',
  'AdditionalApplicantDetails.BirthSurname': 'names', 'AdditionalApplicantDetails.BirthSurnameUntil': 'names',
  'AdditionalApplicantDetails.OtherSurnames': 'names', 'AdditionalApplicantDetails.OtherForenames': 'names',
  'Gender': 'identity', 'NINumber': 'identity', 'AdditionalApplicantDetails.ContactNumber': 'identity', 'AdditionalApplicantDetails.LanguagePreference': 'identity',
  'DateOfBirth': 'birth', 'AdditionalApplicantDetails.BirthTown': 'birth', 'AdditionalApplicantDetails.BirthCounty': 'birth',
  'AdditionalApplicantDetails.BirthCountry': 'birth', 'AdditionalApplicantDetails.BirthNationality': 'birth',
  'CurrentAddress': 'addresses', 'PreviousAddress': 'addresses',
  'ApplicantIdentityDetails': 'documents',
}

/** The page a field is entered on, or undefined for the declaration fields (confirm screen) and the containers */
export const pageOfField = (xsdPath: string): Page | undefined => {
  const plain = xsdPath.replace(/\[\]/g, '')
  const keys = Object.keys(FIELD_PAGES)
  for (let i = 0; i < keys.length; i++) {
    if (plain === keys[i] || plain.indexOf(`${keys[i]}.`) === 0) return FIELD_PAGES[keys[i]]
  }
  return undefined
}

/** Human labels for messages, keyed by the XSD path (array markers kept) */
const FIELD_LABELS: Record<string, string> = {
  'Title': 'title', 'Forename': 'forename', 'Middlenames.Middlename[]': 'middle name', 'PresentSurname': 'surname',
  'CurrentAddress.Address.AddressLine1': 'address line 1', 'CurrentAddress.Address.AddressLine2': 'address line 2',
  'CurrentAddress.Address.AddressTown': 'address town', 'CurrentAddress.Address.AddressCounty': 'address county',
  'CurrentAddress.Address.Postcode': 'postcode', 'CurrentAddress.Address.CountryCode': 'country',
  'CurrentAddress.ResidentFromGyearMonth': 'resident from date',
  'PreviousAddress[].Address.AddressLine1': 'address line 1', 'PreviousAddress[].Address.AddressLine2': 'address line 2',
  'PreviousAddress[].Address.AddressTown': 'address town', 'PreviousAddress[].Address.AddressCounty': 'address county',
  'PreviousAddress[].Address.Postcode': 'postcode', 'PreviousAddress[].Address.CountryCode': 'country',
  'PreviousAddress[].ResidentDates.ResidentFromGyearMonth': 'resident from date', 'PreviousAddress[].ResidentDates.ResidentToGyearMonth': 'resident to date',
  'DateOfBirth': 'date of birth', 'Gender': 'gender', 'NINumber': 'National Insurance number',
  'AdditionalApplicantDetails.BirthSurname': 'birth surname', 'AdditionalApplicantDetails.BirthSurnameUntil': 'birth surname used until year',
  'AdditionalApplicantDetails.OtherSurnames.OtherSurname[].Name': 'surname', 'AdditionalApplicantDetails.OtherSurnames.OtherSurname[].UsedFrom': 'used from year',
  'AdditionalApplicantDetails.OtherSurnames.OtherSurname[].UsedTo': 'used to year',
  'AdditionalApplicantDetails.OtherForenames.OtherForename[].Name': 'forename', 'AdditionalApplicantDetails.OtherForenames.OtherForename[].UsedFrom': 'used from year',
  'AdditionalApplicantDetails.OtherForenames.OtherForename[].UsedTo': 'used to year',
  'AdditionalApplicantDetails.BirthTown': 'birth town', 'AdditionalApplicantDetails.BirthCounty': 'birth county',
  'AdditionalApplicantDetails.BirthCountry': 'birth country', 'AdditionalApplicantDetails.BirthNationality': 'birth nationality',
  'AdditionalApplicantDetails.ContactNumber': 'contact number', 'AdditionalApplicantDetails.UnspentConvictions': 'unspent convictions answer',
  'AdditionalApplicantDetails.DeclarationByApplicant': 'declaration', 'AdditionalApplicantDetails.LanguagePreference': 'language preference',
  'ApplicantIdentityDetails.IdentityVerified': 'identity verified answer', 'ApplicantIdentityDetails.EvidenceCheckedBy': 'evidence checked by',
  'ApplicantIdentityDetails.PassportDetails.PassportNumber': 'passport number', 'ApplicantIdentityDetails.PassportDetails.PassportDob': 'passport date of birth',
  'ApplicantIdentityDetails.PassportDetails.PassportNationality': 'passport nationality', 'ApplicantIdentityDetails.PassportDetails.PassportIssueDate': 'passport issue date',
  'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber': 'driver licence number', 'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceDOB': 'driver licence date of birth',
  'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceType': 'driver licence type', 'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceValidFrom': 'driver licence valid from date',
  'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceIssueCountry': 'driver licence issue country',
}

export const fieldLabel = (xsdPath: string): string => {
  const label = FIELD_LABELS[xsdPath]
  if (label !== undefined) return label
  const last = xsdPath.replace(/\[\]/g, '').split('.').pop() ?? xsdPath
  return last.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
}

export type FieldInstance = { path: IssuePath, value: unknown }

/**
 * Every concrete instance of an XSD path in the data: `PreviousAddress[].Address.Postcode` yields one per previous
 * address. An instance is yielded only when its parent exists, so a missing optional block produces nothing for its
 * children (the block's own entry reports it, when required).
 */
export const instancesOf = (data: unknown, xsdPath: string): FieldInstance[] => {
  const out: FieldInstance[] = []
  const walk = (node: unknown, segments: string[], path: IssuePath) => {
    if (segments.length === 0) return
    const segment = segments[0]
    const rest = segments.slice(1)
    const isArray = segment.endsWith('[]')
    const xsdKey = isArray ? segment.substring(0, segment.length - 2) : segment
    const key = DATA_KEYS[xsdKey] ?? xsdKey
    const value = node !== null && typeof node === 'object' ? (node as Record<string, unknown>)[key] : undefined
    if (isArray) {
      if (Array.isArray(value)) {
        if (rest.length === 0) {
          value.forEach((item, i) => out.push({ path: [...path, key, i], value: item }))
        } else {
          value.forEach((item, i) => walk(item, rest, [...path, key, i]))
        }
      } else if (rest.length === 0) {
        out.push({ path: [...path, key], value })
      }
      return
    }
    if (rest.length === 0) {
      out.push({ path: [...path, key], value })
    } else if (value !== null && typeof value === 'object') {
      walk(value, rest, [...path, key])
    }
  }
  walk(data, xsdPath.split('.'), [])
  return out
}

type Compiled = XsdFacets & { base: string, regex?: RegExp, upperCaseLetters: boolean }
const compiledCache: Record<string, Compiled> = {}

/** The facets that apply to a field: its type's, overridden by any inline restriction */
export const compiledFacets = (field: XsdField): Compiled => {
  const key = `${field.type}|${JSON.stringify(field.facets ?? {})}`
  const cached = compiledCache[key]
  if (cached) return cached
  const type = (XSD_SIMPLE_TYPES as Record<string, XsdFacets & { base?: string }>)[field.type] ?? {}
  const facets: XsdFacets = { ...type, ...(field.facets ?? {}) }
  const pattern = facets.pattern
  const compiled: Compiled = {
    ...facets,
    base: type.base ?? 'xsd:string',
    // An XSD pattern matches the whole value; JavaScript's does not unless anchored (DBS-237, DBS-259)
    regex: pattern === undefined ? undefined : new RegExp(`^(?:${pattern})$`),
    // Types written in upper case only: the portals upper-case on save, so the pattern is tested case-insensitively
    // and lower case is reported separately on the server (text.upperCase)
    upperCaseLetters: pattern !== undefined && pattern.indexOf('A-Z') >= 0 && pattern.indexOf('a-z') < 0,
  }
  compiledCache[key] = compiled
  return compiled
}

/** The simple-typed applicant fields whose values must be upper case */
export const upperCaseFields = (): XsdField[] =>
  XSD_APPLICANT_FIELDS.filter(f => f.kind === 'simple' && compiledFacets(f).upperCaseLetters)

/**
 * Length, pattern and permitted-value problems for every present value of the applicant fields on `pages` (all
 * fields when `pages` is undefined). Values that are not strings are left to the required check and the type system.
 */
export const fieldConstraintIssues = (data: unknown, pages: Page[] | undefined): RawIssue[] => {
  const issues: RawIssue[] = []
  for (let f = 0; f < XSD_APPLICANT_FIELDS.length; f++) {
    const field = XSD_APPLICANT_FIELDS[f]
    const page = pageOfField(field.path)
    if (pages !== undefined && (page === undefined || pages.indexOf(page) < 0)) continue
    const label = fieldLabel(field.path)
    const instances = instancesOf(data, field.path)
    if (field.kind === 'complex') {
      if (field.maxOccurs !== undefined) {
        // A repeating block: the instance list is the array's items
        if (instances.length > field.maxOccurs) {
          const parentPath = instances[0].path.slice(0, -1)
          issues.push({ path: parentPath, message: message('field.tooMany', label, field.maxOccurs) })
        }
      }
      continue
    }
    if (field.maxOccurs !== undefined && instances.length > field.maxOccurs && instances[0] !== undefined) {
      issues.push({ path: instances[0].path.slice(0, -1), message: message('field.tooMany', label, field.maxOccurs) })
    }
    const facets = compiledFacets(field)
    for (let i = 0; i < instances.length; i++) {
      const { path, value } = instances[i]
      if (!present(value) || typeof value !== 'string') continue
      if (facets.length !== undefined && value.length !== facets.length) {
        issues.push({ path, message: message('field.length', label, facets.length) })
        continue
      }
      if (facets.maxLength !== undefined && value.length > facets.maxLength) {
        issues.push({ path, message: message('field.tooLong', label, facets.maxLength, value.length) })
        continue
      }
      if (facets.minLength !== undefined && facets.minLength > 1 && value.length < facets.minLength) {
        issues.push({ path, message: message('field.tooShort', label, facets.minLength) })
        continue
      }
      if (facets.enumeration !== undefined) {
        if (facets.enumeration.indexOf(value) < 0) issues.push({ path, message: message('field.enum', label) })
        continue
      }
      if (facets.regex !== undefined) {
        const tested = facets.upperCaseLetters ? value.toUpperCase() : value
        if (!facets.regex.test(tested)) { issues.push({ path, message: message('field.pattern', label) }); continue }
      }
      // The XSD date types also require a real calendar date, which the pattern alone does not
      if (!isValidCalendarValue(facets.base, value)) issues.push({ path, message: message('field.pattern', label) })
    }
  }
  return issues
}

const daysInMonth = (year: number, month: number): number => new Date(Date.UTC(year, month, 0)).getUTCDate()

/** True when a value of an XSD date-like base type is a real date (the pattern only checks the digits) */
export const isValidCalendarValue = (base: string, value: string): boolean => {
  if (base === 'xsd:date') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) return true
    const year = parseInt(m[1], 10), month = parseInt(m[2], 10), day = parseInt(m[3], 10)
    return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month)
  }
  if (base === 'xsd:gYearMonth') {
    const m = value.match(/^(\d{4})-(\d{2})$/)
    if (!m) return true
    const month = parseInt(m[2], 10)
    return month >= 1 && month <= 12
  }
  return true
}

/** Lower-case letters in a field the DBS requires in upper case */
export const upperCaseIssues = (data: unknown): RawIssue[] => {
  const issues: RawIssue[] = []
  const fields = upperCaseFields()
  for (let f = 0; f < fields.length; f++) {
    const instances = instancesOf(data, fields[f].path)
    for (let i = 0; i < instances.length; i++) {
      const { path, value } = instances[i]
      if (typeof value === 'string' && /[a-z]/.test(value)) issues.push({ path, message: message('text.upperCase') })
    }
  }
  return issues
}

/**
 * Required elements that are missing or empty, judged as the XSD would for a finished application: a required field
 * inside an optional block is only required when the block is present.
 */
export const requiredFieldIssues = (data: unknown): RawIssue[] => {
  const issues: RawIssue[] = []
  for (let f = 0; f < XSD_APPLICANT_FIELDS.length; f++) {
    const field = XSD_APPLICANT_FIELDS[f]
    if (!field.required) continue
    const instances = instancesOf(data, field.path)
    for (let i = 0; i < instances.length; i++) {
      const { path, value } = instances[i]
      const missing = field.kind === 'complex'
        ? value === null || value === undefined || (Array.isArray(value) && value.length === 0)
        : !present(value)
      if (missing) issues.push({ path, message: message('field.required') })
    }
  }
  return issues
}
